import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { registraAziendaEOwner } from "../../../lib/services/authService";

const NuovaAzienda = ({ onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    cf: '',
    nome: '',
    cognome: '',
    nomeAzienda: '',
    pIva: '',
    indirizzo: '',
    telefono: '',
    plan_type: 'basic',
    max_employees: 10,
    storage_limit_gb: 5,
    subscription_start: new Date().toISOString().split('T')[0],
    subscription_end: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handlePlanChange = (e) => {
    const plan = e.target.value;
    let limits = { max_employees: 10, storage_limit_gb: 5 };

    if (plan === 'premium') {
      limits = { max_employees: 50, storage_limit_gb: 15 };
    } else if (plan === 'enterprise') {
      limits = { max_employees: 9999, storage_limit_gb: 100 };
    }

    setFormData(prev => ({ ...prev, plan_type: plan, ...limits }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = t('create_company.errors.email_req');
    if (!formData.cf) newErrors.cf = t('create_company.errors.cf_req');
    if (!formData.nome) newErrors.nome = t('create_company.errors.name_req');
    if (!formData.nomeAzienda) newErrors.nomeAzienda = t('create_company.errors.company_req');
    if (!formData.subscription_end) newErrors.subscription_end = t('create_company.errors.date_req');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        password: formData.cf,
        confirmPassword: formData.cf,
        ruolo: 'superAdmin',
        is_active: true
      };

      await registraAziendaEOwner(payload);
      
      alert(t('create_company.success'));
      if (onSuccess) onSuccess();
      
      setFormData({ 
        email: '', cf: '', nome: '', cognome: '', 
        nomeAzienda: '', pIva: '', indirizzo: '', telefono: '',
        plan_type: 'basic', max_employees: 10, storage_limit_gb: 5,
        subscription_start: new Date().toISOString().split('T')[0],
        subscription_end: ''
      });
    } catch (err) {
      setErrors({ general: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden">
      <div className="p-6 border-b border-border bg-muted/30">
        <div className="flex items-center gap-4 mb-2">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors text-sm font-medium">
            <Icon name="ArrowLeft" size={16} />
            {t('create_company.back')}
          </button>
        </div>
        <h2 className="text-xl font-bold text-foreground">{t('create_company.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('create_company.subtitle')}</p>
      </div>
      
      <div className="p-6">
        {errors.general && <p className="text-red-500 text-sm mb-4 text-center p-2 bg-red-50 rounded">{errors.general}</p>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* 1. ADMIN SEKCIJA */}
            <div className="space-y-4 bg-muted/10 p-4 rounded-xl border border-border/50">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-primary flex items-center gap-2">
                <Icon name="User" size={16} /> {t('create_company.admin_section')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <Input name="nome" label={t('create_company.labels.name')} value={formData.nome} onChange={handleChange} error={errors.nome} required />
                <Input name="cognome" label={t('create_company.labels.surname')} value={formData.cognome} onChange={handleChange} />
              </div>
              <Input name="cf" label={t('create_company.labels.cf')} value={formData.cf} onChange={handleChange} error={errors.cf} required />
              <Input name="telefono" label={t('create_company.labels.phone')} value={formData.telefono} onChange={handleChange} />
              <Input type="email" name="email" label={t('create_company.labels.email')} value={formData.email} onChange={handleChange} error={errors.email} required />
              
              <p className="text-[10px] text-muted-foreground italic leading-tight pt-2 border-t border-border/50">
                {t('create_company.info.password_auto')}
              </p>
            </div>

            {/* 2. FIRMA SEKCIJA */}
            <div className="space-y-4 bg-muted/10 p-4 rounded-xl border border-border/50">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-primary flex items-center gap-2">
                <Icon name="Building" size={16} /> {t('create_company.company_section')}
              </h3>
              <Input name="nomeAzienda" label={t('create_company.labels.company_name')} value={formData.nomeAzienda} onChange={handleChange} error={errors.nomeAzienda} required />
              <Input name="indirizzo" label={t('create_company.labels.address')} value={formData.indirizzo} onChange={handleChange} required />
              <Input name="pIva" label={t('create_company.labels.vat')} value={formData.pIva} onChange={handleChange} />
            </div>

            {/* 3. PAKET SEKCIJA */}
            <div className="space-y-4 bg-primary/5 p-4 rounded-xl border border-primary/20">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-primary flex items-center gap-2">
                <Icon name="CreditCard" size={16} /> {t('create_company.plan_section')}
              </h3>
              
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-muted-foreground uppercase">{t('create_company.labels.plan')}</label>
                <select 
                  name="plan_type" 
                  value={formData.plan_type} 
                  onChange={handlePlanChange}
                  className="w-full h-10 px-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary outline-none transition-all text-sm"
                >
                  <option value="basic">{t('create_company.plans.basic')}</option>
                  <option value="premium">{t('create_company.plans.premium')}</option>
                  <option value="enterprise">{t('create_company.plans.enterprise')}</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col">
                   <Input 
                    type="date" 
                    name="subscription_start" 
                    label={t('create_company.labels.start')} 
                    value={formData.subscription_start} 
                    onChange={handleChange} 
                    required 
                    className="text-xs" 
                  />
                </div>
                <div className="flex flex-col">
                  <Input 
                    type="date" 
                    name="subscription_end" 
                    label={t('create_company.labels.end')} 
                    value={formData.subscription_end} 
                    onChange={handleChange} 
                    error={errors.subscription_end} 
                    required 
                    className="text-xs"
                  />
                </div>
              </div>

              <div className="p-3 bg-background rounded-lg border border-border space-y-2">
                <div className="flex justify-between text-[11px]">
                  <span>{t('create_company.labels.max_emp')}</span>
                  <span className="font-bold text-primary">{formData.max_employees === 9999 ? '∞' : formData.max_employees}</span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span>{t('create_company.labels.cloud_space')}</span>
                  <span className="font-bold text-primary">{formData.storage_limit_gb} GB</span>
                </div>
              </div>

              <div className="pt-2">
                <Button type="submit" fullWidth loading={isLoading} size="lg">
                  {t('create_company.button')}
                </Button>
              </div>
            </div>

          </div>
        </form>
      </div>
    </div>
  );
};

export default NuovaAzienda;